import { RnbCalculatorPayload } from 'typings'
import { toAPIOriginalValues, toAPIPromotionBase } from '..'
import type {
  BuyTogetherConditions,
  BuyTogetherEffect,
  BuyTogetherPromotion,
} from '../../../../typings/buyTogether'
import {
  toAPIBin,
  toAPICluster,
  toAPIOrderValue,
  toAPIPaymentMethods,
  toAPIUtm,
} from '../conditions'
import { toAPIRestrictions } from '../restrictions'

const BUY_TOGETHER_INCOMPATIBLE_FIELDS: (keyof RnbCalculatorPayload)[] = [
  'absoluteShippingDiscountValue',
  'brands',
  'brandsAreInclusive',
  'categories',
  'categoriesAreInclusive',
  'discountExpression',
  'maximumUnitPriceDiscount',
  'nominalDiscountType',
  'nominalDiscountValue',
  'nominalRewardValue',
  'nominalShippingDiscountValue',
  'nominalTax',
  'orderStatusRewardValue',
  'percentualDiscountValue',
  'percentualRewardValue',
  'percentualShippingDiscountValue',
  'percentualTax',
  'priceTableName',
  'products',
  'productsAreInclusive',
  'rebatePercentualDiscountValue',
  'shippingPercentualTax',
  'skus',
  'skusAreInclusive',
  'skusGift',
  'taxExpression',
]

type APIBuyTogetherSectionPayload = Pick<
  Partial<RnbCalculatorPayload>,
  | 'listSku1BuyTogether'
  | 'listSku2BuyTogether'
  | 'percentualDiscountValueList1'
  | 'percentualDiscountValueList2'
  | 'minimumQuantityBuyTogether'
>

type APIBuyTogetherConditions = Pick<
  Partial<RnbCalculatorPayload>,
  | 'paymentsMethods'
  | 'restrictionsBins'
  | 'totalValueCeling'
  | 'totalValueFloor'
  | 'totalValueMode'
  | 'utmSource'
  | 'utmCampaign'
  | 'clusterExpressions'
  | 'clusterOperator'
>

export function toAPIBuyTogetherSection(
  config: BuyTogetherEffect
): APIBuyTogetherSectionPayload {
  const { trigger, benefit } = config
  if (!trigger || !benefit)
    throw new Error(
      'Cannot convert Promotion to API Payload: Invalid Buy Together Effect',
      { cause: { payload: config } }
    )

  return {
    listSku1BuyTogether: trigger.skus,
    listSku2BuyTogether: benefit.skus,
    percentualDiscountValueList1: trigger.discount,
    percentualDiscountValueList2: benefit.discount,
    minimumQuantityBuyTogether: trigger.minQuantity,
  }
}

export function toAPIBuyTogetherConditions(
  conditions: BuyTogetherConditions
): APIBuyTogetherConditions {
  return {
    ...toAPIPaymentMethods(conditions.paymentMethods),
    ...toAPIBin(conditions.bin),
    ...toAPIOrderValue(conditions.orderValue),
    ...toAPIUtm(conditions.utm),
    ...toAPICluster(conditions.cluster),
  }
}

type APIBuyTogetherPayload = ReturnType<
  typeof toAPIOriginalValues &
    typeof toAPIBuyTogetherSection &
    typeof toAPIRestrictions &
    typeof toAPIBuyTogetherConditions &
    typeof toAPIPromotionBase
>

export function toAPIBuyTogether(
  data: BuyTogetherPromotion,
  originalValues?: RnbCalculatorPayload
): APIBuyTogetherPayload {
  return {
    ...toAPIOriginalValues(originalValues, BUY_TOGETHER_INCOMPATIBLE_FIELDS),

    ...toAPIPromotionBase(data),
    ...toAPIBuyTogetherSection(data.buyTogether),
    ...toAPIBuyTogetherConditions(data.conditions),
    ...toAPIRestrictions(data.restrictions),
  }
}
